import {
  DeviceWithInstalledPower,
  DSTMultiCell,
  SpotPriceResult,
  useProductionReduction,
} from '@e2m-customer-portal/domains';
import { useLocale } from '@e2m-customer-portal/hooks';
import { convertMeasure, InputFieldType } from '@e2m-customer-portal/shared';
import clsx from 'clsx';
import {
  addMinutes,
  eachDayOfInterval,
  eachMinuteOfInterval,
  endOfDay,
  getDay,
  getTime,
  getWeek,
  Interval,
  isBefore,
  isWeekend,
  isWithinInterval,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, ClearIcon, WarningIcon } from '../../../../../../assets/icons';
import { ForecastSlice, MarketLocation, SpotPriceForecast } from '../../../../../../generated/graphql';
import { getEarliestMarketingDeadline, getMarketingDeadline, isAfterDeadline } from '../../../../../../utils';
import { SpotPriceLabel } from '../../SpotPriceLabel';

const DST_CORRECTION_ID = 'dst-correction';

const SCROLL_TIMEOUT = 500;

export interface DeviceTableBodyMobileProps {
  marketLocation: MarketLocation;
  device: DeviceWithInstalledPower;
  interval: Interval;
  slices: readonly ForecastSlice[];
  spotPriceForecast?: SpotPriceForecast;
  spotPrice: (sliceDate: Date) => SpotPriceResult | undefined;
  isFifteenMinutesResolution?: boolean;
  isInSliceSelectionMode?: boolean;
  sliceSelection: Array<number>;
  setSliceSelection: React.Dispatch<React.SetStateAction<number[]>>;
  assistantDate: Date;
  setAssistantDate: React.Dispatch<React.SetStateAction<Date>>;
}

export function DeviceTableBodyMobile({
  marketLocation,
  device,
  interval,
  slices,
  spotPriceForecast,
  spotPrice: getSpotPrice,
  isFifteenMinutesResolution,
  isInSliceSelectionMode,
  sliceSelection,
  setSliceSelection,
  assistantDate,
  setAssistantDate,
}: Readonly<DeviceTableBodyMobileProps>) {
  const [t] = useTranslation();
  const daysInForecast = eachDayOfInterval(interval).filter((date) => getTime(date) !== getTime(interval.end));
  const { code: locale } = useLocale();
  const { getAverageFormatted } = useProductionReduction(
    slices,
    daysInForecast,
    isFifteenMinutesResolution,
    undefined,
    {
      slicesAreSortedByHour: true,
    },
  );

  const latestHistoricalSpotPrice = spotPriceForecast?.latestHistoricalSpotPrice;

  useEffect(() => {
    const today = new Date();
    const isCurrentWeek =
      getWeek(startOfDay(today), { weekStartsOn: 1 }) === getWeek(interval.start, { weekStartsOn: 1 });
    if (isCurrentWeek) {
      setTimeout(() => {
        // This is purely cosmetic and can only fail in unit tests,
        // so we don’t need serious error handling here
        try {
          document
            .querySelector(`#col-${getDay(today)}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        } catch (error) {
          console.error(error);
        }
      }, SCROLL_TIMEOUT);
    } else {
      // this resets the position to monday when the interval changes to another week with the table view open
      setTimeout(
        () =>
          document.querySelector(`#col-1`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' }),
        SCROLL_TIMEOUT,
      );
    }
  }, [interval]);

  useEffect(() => {
    const deadline = getMarketingDeadline(getEarliestMarketingDeadline(device.deadlines));
    if (isWithinInterval(deadline, interval) && isInSliceSelectionMode) {
      setTimeout(
        () =>
          document
            .querySelector(`#col-${getDay(deadline)}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' }),
        SCROLL_TIMEOUT,
      );
    }
  }, [device.deadlines, interval, isInSliceSelectionMode]);

  const revokeDowntimeClick = (slice: ForecastSlice) => {
    setAssistantDate(startOfDay(slice.date));
    setSliceSelection((sliceSelection) => {
      const hour = slice.date.getHours();
      if (sliceSelection.includes(slice.date.getHours())) return sliceSelection.filter((h) => h !== hour);
      else return [...sliceSelection, hour];
    });
  };

  return (
    <table
      className="w-full min-w-[1800px] table-auto border-spacing-0 bg-white [container:table_/_size] md:min-w-[2000px]"
      data-testid={device.name}
    >
      <colgroup>
        <col className="max-w-24" span={2} />
        {daysInForecast.map((day) => (
          <col key={`colgroup-${device.id}-${day.getDate()}`} style={{ width: `100cqw` }} />
        ))}
      </colgroup>
      <thead data-testid="mobile-device-table-head" className="relative">
        <tr>
          <th
            className="sticky left-0 max-w-24 border-0 border-b border-r border-solid border-divider bg-white pl-3 text-start text-xs font-semibold "
            colSpan={2}
          >
            {t('MARKET_LOCATION_FORECAST_TABLE_DATE')}
          </th>
          {daysInForecast.map((date) => (
            <th
              key={`date-${device.id}-${date.toISOString()}`}
              id={`col-${getDay(date)}`}
              className={clsx(
                'z-30 border border-l-0 border-t-0 border-solid border-divider py-2 text-sm font-normal leading-4 first:z-50 last:border-r-0',
                {
                  'bg-blue-grey-200/5': isWeekend(date),
                  'text-black/60': isAfterDeadline(getEarliestMarketingDeadline(device.deadlines), date),
                },
              )}
            >
              {date
                .toLocaleDateString(locale, {
                  weekday: 'short',
                  day: '2-digit',
                  month: '2-digit',
                })
                .replace(/(?<=[A-Za-z])\./, '')}
            </th>
          ))}
        </tr>
        <tr>
          <th
            role="rowheader"
            className="sticky left-0 max-w-24 border-0 border-b border-r border-solid border-divider bg-grey-50 text-xs font-semibold"
            data-testid="prod-overview-avg-device"
            colSpan={2}
          >
            {t('MARKET_LOCATION_FORECAST_TABLE_POWER_AVERAGE')}
          </th>
          {daysInForecast.map((date) => (
            <th
              key={`prod-avg-${device.id}-${date.toISOString()}`}
              className={clsx(
                'z-30 border border-l-0 border-t-0 border-solid border-divider py-1 text-sm font-normal first:z-50 last:border-r-0',
                isWeekend(date) ? 'bg-blue-grey-200/10' : 'bg-grey-50',
              )}
            >
              {getAverageFormatted(date)}
            </th>
          ))}
        </tr>
      </thead>

      <tbody data-testid="mobile-device-table-body">
        {eachMinuteOfInterval(
          {
            start: startOfDay(startOfWeek(daysInForecast[0], { weekStartsOn: 1 })),
            end: endOfDay(startOfWeek(daysInForecast[0], { weekStartsOn: 1 })),
          },
          { step: isFifteenMinutesResolution ? 15 : 60 },
        ).map((date, row) => {
          const rowSlices = isFifteenMinutesResolution
            ? slices
                .filter((slice) => slice.date.getHours() === date.getHours())
                .filter((slice) => slice.date.getMinutes() === date.getMinutes())
            : slices.filter((slice) => slice.date.getHours() === date.getHours());

          // Here add handling for DST Winter Time in 15min resolution table

          // Add an empty slice for DST shenanigans
          if (rowSlices.length < daysInForecast.length) {
            const dstIndex =
              rowSlices.findLastIndex(
                (slice, sliceIndex) => daysInForecast[sliceIndex].getDate() === slice.date.getDate(),
              ) + 1;
            rowSlices.splice(dstIndex, 0, {
              date: new Date(daysInForecast[dstIndex].setHours(row)),
              fields: [],
              hasDowntime: false,
              id: DST_CORRECTION_ID,
            });
          }

          return (
            <tr key={`row-${device.id}-${date.toISOString()}`} className="even:bg-grey-50">
              <th
                key={`slice-time-${device.id}-${date.toISOString()}`}
                className={clsx(
                  'sticky left-0 z-30 min-w-24 border-0 border-b border-r border-solid border-grey-100 pl-4 text-start text-sm font-normal leading-4 ',
                  row % 4 === 3 ? 'border-b-blue-grey-100' : 'border-b-grey-100',
                  row % 2 === 0 ? 'bg-white' : 'bg-grey-50',
                )}
                colSpan={2}
              >
                {`${date.toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                })} - ${addMinutes(date, isFifteenMinutesResolution ? 15 : 60).toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  hourCycle: date.getHours() < 12 ? 'h23' : 'h24',
                })}`}
              </th>

              {rowSlices.map((slice, column) => {
                const spotPrice = getSpotPrice(slice.date);
                const timeSliceIsUnavailable =
                  isAfterDeadline(getEarliestMarketingDeadline(device.deadlines), slice.date) ||
                  (marketLocation.marketingEnd != null && !isBefore(slice.date, marketLocation.marketingEnd));
                const isInAssistantSelection =
                  assistantDate.getTime() === startOfDay(slice.date).getTime() &&
                  sliceSelection.includes(slice.date.getHours());

                return (
                  <Fragment key={`slice-${device.id}-${slice.id}`}>
                    {column + 1 < rowSlices.length &&
                    slice.date.getDate() === rowSlices[column + 1].date.getDate() &&
                    slice.date.getTimezoneOffset() !== rowSlices[column + 1].date.getTimezoneOffset() ? (
                      <DSTMultiCell
                        sliceValues={[{ slice }, { slice: rowSlices[column + 1] }]}
                        firstSliceDate={slice.date}
                        showSpotPrices={true}
                        spotPrice={getSpotPrice}
                        latestHistoricalSpotPrice={
                          spotPriceForecast?.latestHistoricalSpotPrice as Date | null | undefined
                        }
                        cellIndex={{ row, column }}
                        sliceIDs={[slice.id, rowSlices[column + 1].id]}
                        editedSlices={new Set()}
                      />
                    ) : column - 1 > 0 &&
                      rowSlices[column - 1].date.getDate() === slice.date.getDate() &&
                      rowSlices[column - 1].date.getTimezoneOffset() !== slice.date.getTimezoneOffset() ? (
                      <></>
                    ) : slice.id !== DST_CORRECTION_ID ? (
                      <td
                        className={clsx(
                          'group/cell relative h-6 snap-start scroll-ml-0.5 border-0 border-b border-solid p-0 text-center first:snap-none',
                          row % 4 === 3 ? 'border-b-blue-grey-100' : 'border-b-grey-100',
                          { 'bg-blue-grey-200/5': isWeekend(slice.date) },
                        )}
                      >
                        <button
                          className={clsx(
                            'group/downtime box-border flex h-6 w-full appearance-none items-center justify-center border-0 border-r border-solid border-grey-100 text-black',
                            isInSliceSelectionMode && isInAssistantSelection ? 'bg-green-200/15' : 'bg-transparent',
                            {
                              'pointer-events-none': !isInSliceSelectionMode,
                              'opacity-60': isInSliceSelectionMode && timeSliceIsUnavailable,
                            },
                          )}
                          disabled={timeSliceIsUnavailable}
                          onClick={() => revokeDowntimeClick(slice)}
                        >
                          {spotPrice?.price !== undefined && (
                            <div
                              className={clsx([
                                'spot pointer-events-none absolute inset-y-0 left-0 flex aspect-[1.75] items-center justify-center -tracking-wide border-0 border-r border-solid border-grey-100 text-xs',
                                spotPrice.gradient !== undefined ? `bg-heatmap-${spotPrice.gradient}` : 'bg-grey-200',
                                {
                                  'text-white':
                                    spotPrice.gradient !== undefined &&
                                    (spotPrice.gradient < 1 || spotPrice.gradient > 8),
                                  'saturate-50':
                                    latestHistoricalSpotPrice && isBefore(slice.date, latestHistoricalSpotPrice),
                                },
                              ])}
                            >
                              <SpotPriceLabel spotPrice={spotPrice.price} unit="€" locale={locale} />
                            </div>
                          )}
                          <div className="w-[calc(100%-5rem)] border-none bg-transparent px-10 py-0 text-center text-xs leading-6 text-black">
                            {slice.hasDowntime ? (
                              <div className="m-0 flex w-full items-center justify-center border-none bg-transparent p-0">
                                <WarningIcon className="text-black/70" />
                              </div>
                            ) : (
                              convertMeasure(
                                slice.fields.find((value) => value.type === InputFieldType.PRODUCTION)?.value ?? {
                                  value: null,
                                  unit: 'kW',
                                },
                                'kW',
                              ).value
                            )}
                          </div>
                          {isInSliceSelectionMode && !timeSliceIsUnavailable && (
                            <div
                              className={clsx(
                                'absolute inset-y-0 right-0 flex aspect-[1.75] items-center justify-center border-0 border-l border-solid border-grey-100 text-xs',
                                isInAssistantSelection ? 'bg-green-200/15' : 'bg-grey-50',
                              )}
                            >
                              {isInAssistantSelection ? (
                                <CheckIcon className="text-green-200" />
                              ) : (
                                <ClearIcon className="text-grey-300" />
                              )}
                            </div>
                          )}
                        </button>
                      </td>
                    ) : (
                      // An empty cell for DST shenanigans
                      <td
                        className={clsx(
                          'group/cell relative border-0 border-b border-solid p-0 text-center',
                          row % 4 === 3 ? 'border-b-blue-grey-100' : 'border-b-grey-100',
                          { 'bg-blue-grey-200/5': isWeekend(slice.date) },
                        )}
                      >
                        –
                      </td>
                    )}
                  </Fragment>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
